import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { AuthCard, AuthMessage, authButtonCls } from "./components/AuthCard";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const fired = useRef("");
  const [state, setState] = useState({ status: token ? "checking" : "invalid", message: "", email: "" });

  useEffect(() => {
    if (!token || fired.current === token) return;
    fired.current = token;
    api.post("/auth/change-email/verify", { token })
      .then((r) => setState({ status: "done", message: r.data.message, email: r.data.email }))
      .catch((err) => setState((s) => (s.status === "done" ? s : { status: "invalid", message: formatApiError(err), email: "" })));
  }, [token]);

  if (state.status === "checking") {
    return (
      <AuthCard testid="verify-email-card" title="Confirming your new login email">
        <p className="text-sm text-slate-500" data-testid="verify-email-checking">One moment…</p>
      </AuthCard>
    );
  }

  if (state.status === "done") {
    return (
      <AuthCard testid="verify-email-card" title="Login email updated">
        <div className="flex items-start gap-3 rounded-2xl bg-white border border-sand p-4" data-testid="verify-email-success">
          <CheckCircle2 className="w-6 h-6 shrink-0 text-[#15803D]" strokeWidth={2.2} />
          <p className="text-sm font-semibold text-navy">{state.message}</p>
        </div>
        <p className="mt-4 text-xs text-slate-500">Your password is unchanged. A security notice was sent to your previous email address.</p>
        <Link to="/admin/login" data-testid="verify-go-to-login" className={`${authButtonCls} mt-6 inline-flex items-center justify-center`}>GO TO SIGN IN</Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard testid="verify-email-card" title="Link expired">
      <AuthMessage testid="verify-email-invalid">{state.message || "This verification link is invalid, has expired, or was already used."}</AuthMessage>
      <p className="mt-4 text-xs text-slate-500">Your login email has not changed. Sign in and start the change again from Settings → Account &amp; Security.</p>
      <Link to="/admin/login" data-testid="back-to-login-link" className={`${authButtonCls} mt-5 inline-flex items-center justify-center`}>BACK TO SIGN IN</Link>
    </AuthCard>
  );
}
